<template>
  <div>
    <div class="spl-dashboard">
      <main-header
        :dashboard-data="dashboardData" />

      <dashboard-header-summary
        v-if="$route.query.newly_logged_in"
        :dashboard-data="dashboardData" />

      <parcel-filter
        filter-name="dashboard"
        @onReload="loadDashboardList"
        @onSearch="searchClicked" />
      <div class="spl-dashboard-list">
        <!-- *********************** Controls for the dashboard *********************************** -->
        <div class="spl-dashboard-list__controls">
          <div class="spl-dashboard-list__controls__customer-country">
            <!-- *********************** Customer Country Switch *********************************** -->
            <div>
              <form-radiobutton-group
                :options="[{label: 'Country', value: 'Country'}, {label: 'Customer', value: 'Customer'}]"
                :horizontal="true"
                :toggle="false"
                :value="groupBy"
                class="spl-dashboard-list__controls__group-by"
                @radiobuttonChanged="group_by_changed" />
            </div>

            <form-checkbox
              :checked="showOverview"
              class="spl-dashboard-list__controls__overview"
              @checkboxChanged="overviewChanged">
              Show Overview
            </form-checkbox>

            <!--form-radiobutton-group value="forward" :options="[{label: 'Forward', value: 'forward'}, {label: 'Return', value: 'return'}]" :horizontal="true" :toggle="true"></form-radiobutton-group-->
          </div>
          <div>
            <form-button @onClick="copyHTML">
              Copy
            </form-button>
          </div>
          <!-- *********************** Column selection *********************************** -->
          <div>
            <list-controls
              v-if="$mq !== 'sm'"
              :column-groups="listHeaderColumnGroups"
              :parcel-list-expanded="parcelListExpanded"
              active-page="dashboard"
              @toggleColumn="toggleColumn"
              @toggleSaveTemplate="toggleSaveTemplate" />
          </div>
        </div>

        <div v-show="!loadingParcels">
          <dashboard-table
            ref="dashboardTable"
            :list-header-column-groups="$mq !== 'sm' ? listHeaderColumnGroups : mobileListHeaderColumnGroups"
            :group-by="groupBy"
            :show-overview="showOverview"
            :parcel-list-expanded="parcelListExpanded" />
        </div>
      </div>

      <save-template-bar v-if="savingTemplate" @closeSaveTemplate="closeSaveTemplate" />
    </div>

    <message-modal />
    <div v-show="loadingParcels" class="spl-load-img" />
  </div>
</template>

<script lang="ts">
import Helpers from '@/helpers.vue'
import webApi from '@/webApi'
import constants from '@/constants'
import { mapGetters, mapMutations } from 'vuex'
import { filter, isEmpty } from 'lodash'

export default {
  name: 'Dashboard',
  mixins: [Helpers],
  data () {
    const listHeaderColumnGroupsOrig = [
      {
        label: 'General',
        columns: [
          { name: constants.DASHBOARD.FIELD_TOTAL, label: 'Total', selected: true, showInReturn: true },
          { name: constants.DASHBOARD.FIELD_HAS_REPORTED_EXCEPTION, label: 'Reported Exception', isError: true, selected: true, showInReturn: true }
        ]
      },
      {
        label: 'Precarriage',
        columns: [
          { name: constants.DASHBOARD.FIELD_MISSING_INFO, label: 'Missing Info', selected: true, isError: true, showInReturn: false }, // red
          { name: constants.DASHBOARD.FIELD_COLLECTED, label: 'Collected', selected: true, showInReturn: true },
          { name: constants.DASHBOARD.FIELD_NOT_COLLECTED, label: 'Not Collected', selected: true, isError: true, showInReturn: true }, // red
          { name: constants.DASHBOARD.FIELD_NOT_BOOKED, label: 'Not Booked', selected: true, isWarning: true, showInReturn: false }, // blue
          { name: constants.DASHBOARD.FIELD_IN_DESTINATION_COUNTRY, label: 'In Country', selected: true, showInReturn: false }
        ]
      },
      {
        label: 'Distribution',
        columns: [
          { name: constants.DASHBOARD.FIELD_NOT_IN_DESTINATION_COUNTRY, label: 'In Precarriage', selected: false, showInReturn: false },
          { name: constants.DASHBOARD.FIELD_AT_PROVIDER, label: 'At Provider', selected: false, showInReturn: true },
          { name: constants.DASHBOARD.FIELD_IN_TRANSIT, label: 'In Transit', selected: false, showInReturn: false },
          { name: constants.DASHBOARD.FIELD_OUT_FOR_DELIVERY, label: 'Out For Delivery', selected: false, showInReturn: false }
        ]
      },
      {
        label: 'Delivered',
        columns: [
          { name: constants.DASHBOARD.FIELD_EARLY, label: 'Early', selected: false, showInReturn: false },
          { name: constants.DASHBOARD.FIELD_ON_TIME, label: 'On Time', selected: true, showInReturn: false },
          { name: constants.DASHBOARD.FIELD_LATE, label: 'Late', selected: true, isError: true, showInReturn: false }, // red
          { name: constants.DASHBOARD.FIELD_EXTERNAL_DELAY, label: 'External', selected: false, isWarning: true, showInReturn: false }, // blue
          { name: constants.DASHBOARD.FIELD_DELIVERED, label: 'Delivered', selected: false, showInReturn: true },
          { name: constants.DASHBOARD.FIELD_NOT_DELIVERED, label: 'Not delivered', selected: false, showInReturn: false }
        ]
      },
      {
        label: 'Weight & Dimensions',
        columns: [
          { name: constants.DASHBOARD.FIELD_NET_PERFORMANCE, label: 'Net Performance (%)', selected: true, showInReturn: false },
          { name: constants.DASHBOARD.FIELD_GROSS_PERFORMANCE, label: 'Gross Performance (%)', selected: true, showInReturn: false },
          { name: constants.DASHBOARD.FIELD_WEIGHT, label: 'Weight', selected: true, showInReturn: true },
          { name: constants.DASHBOARD.FIELD_COLLECTED_WEIGHT, label: 'Collected Weight', selected: false, showInReturn: true },
          { name: constants.DASHBOARD.FIELD_COLLECTED_VOLUME, label: 'Collected Volume', selected: false, showInReturn: true },
          { name: constants.DASHBOARD.FIELD_VOLUME, label: 'Volume', selected: true, showInReturn: true }
        ]
      }
    ]

    return {
      savingTemplate: false,
      dashboardData: {},
      listHeaderColumnGroupsOrig: listHeaderColumnGroupsOrig,
      listHeaderColumnGroups: this.cloneArray(listHeaderColumnGroupsOrig),
      groupBy: this.get_group_by_value(),
      showOverview: false,
      parcelListExpanded: false,
      loadingParcels: false,
      unsubscribeParcelFilter: null,
      mobileListHeaderColumnGroups: [
        {
          label: 'General',
          columns: [
            { name: constants.DASHBOARD.FIELD_TOTAL, label: 'Total', selected: true }
          ]
        },
        {
          label: 'Precarriage',
          columns: [
          ]
        },
        {
          label: 'Distribution',
          columns: [
          ]
        },
        {
          label: 'Delivered',
          columns: [
            { name: constants.DASHBOARD.FIELD_LATE, label: 'Late', selected: true, isError: true }, // red
            { name: constants.DASHBOARD.FIELD_DELIVERED, label: 'Delivered', selected: false }
          ]
        },
        {
          label: 'Weight & Dimensions',
          columns: [
          ]
        }
      ]
    }
  },
  computed: {
    ...mapGetters('parcelFilter', ['parcelFilter'])
  },
  mounted () {
    if (!this.userHasPermission(constants.PERMISSION_TYPES.DASHBOARD, constants.PERMISSION_VALUES.EARLY_COLUMN)) {
      for (let i = 0; i < this.listHeaderColumnGroupsOrig.length; i++) {
        const columnGroup = this.listHeaderColumnGroupsOrig[i]
        columnGroup.columns = filter(columnGroup.columns, (column) => {
          return column.name !== constants.DASHBOARD.FIELD_EARLY
        })
      }
    }
    if (!this.userHasPermission(constants.PERMISSION_TYPES.DASHBOARD, constants.PERMISSION_VALUES.GROSS_PERFORMANCE_COLUMN)) {
      for (let i = 0; i < this.listHeaderColumnGroupsOrig.length; i++) {
        const columnGroup = this.listHeaderColumnGroupsOrig[i]
        columnGroup.columns = filter(columnGroup.columns, (column) => {
          return column.name !== constants.DASHBOARD.FIELD_GROSS_PERFORMANCE
        })
      }
    }
    this.load_Column_Setup()
    this.getShowOverviewSetting()
    this.loadDashboardList()
    this.unsubscribeParcelFilter = this.$store.subscribe(this.handleParcelFilterMutation)
  },
  beforeDestroy () {
    if (this.unsubscribeParcelFilter) {
      this.unsubscribeParcelFilter()
    }
  },
  methods: {
    ...mapMutations('parcelFilter', ['setParcelFilter']),
    copyHTML () {
      this.$refs.dashboardTable.copyHTML()
    },
    handleParcelFilterMutation (mutation) {
      if (mutation.type === 'parcelFilter/setParcelFilter') {
        this.loadDashboardList()
      }
    },
    closeSaveTemplate () {
      this.savingTemplate = false
    },
    searchClicked (searchValue) {
      this.$router.push('/parcel-list?search=' + encodeURIComponent(searchValue))
    },
    loadDashboardList () {
      if (this.loadingParcels) {
        return
      }

      this.dashboardData = {}
      this.loadingParcels = true

      webApi.dashboard.get_Dashboard_Report(this.parcelFilter('dashboard'), this.groupBy === 'Country')
        .then((response) => {
          this.dashboardData = response.data
          this.loadingParcels = false
          if (this.$refs.dashboardTable) {
            this.$refs.dashboardTable.createPrecariageData(this.dashboardData)
          }
        }).catch((response) => {
          this.loadingParcels = false
          var exceptionMessage = 'Could not get Dashboard Data'
          if (response.data && response.data.ExceptionMessage) {
            exceptionMessage = response.data.ExceptionMessage
          }
          window.bus.$emit('openErrorModal', exceptionMessage)
        })
    },

    clickOutside (event) {
      if (!this.$el.querySelector('.spl-dashboard-list__filter').contains(event.target)) {
        this.showColumnsIsOpen = false
      }
    },

    toggleSaveTemplate () {
      this.savingTemplate = !this.savingTemplate
      this.saveTemplateIsOpen = !this.saveTemplateIsOpen
    },

    getShowOverviewSetting () {
      var val = null
      if (window.sessionStorage.getItem('dashboard_show_overview') != null) {
        val = window.sessionStorage.getItem('dashboard_show_overview') === 'true'
      } else { // Otherwise return customer grouping
        val = false
      }
      this.showOverview = val
    },

    overviewChanged () {
      this.showOverview = !this.showOverview
      window.sessionStorage.setItem('dashboard_show_overview', this.showOverview)
    },

    // ************************ Set the grouping ******************
    get_group_by_value () {
      // Check if there is a storage for the grouping
      var val = null
      if (window.sessionStorage.getItem('dashboard_group_by') != null) {
        val = window.sessionStorage.getItem('dashboard_group_by')
      } else if (this.$store.state.personalSettings.personalSettings.Default_Grouping != null) { // If  there is no storage there might be personal settings
        val = this.$store.state.personalSettings.personalSettings.Default_Grouping
      } else { // Otherwise return customer grouping
        val = 'Customer'
      }
      //  Set the  storage
      this.$emit('group_by_changed', val)
      return val
    },
    // ************************ Set the new grouping to  storage ******************
    group_by_changed (newValue) {
      this.groupBy = newValue
      window.sessionStorage.setItem('dashboard_group_by', newValue)
      this.loadDashboardList()
    },

    /** *** Table layout  ******/
    load_Column_Setup () {
      const storedHeaderColumnGroups = JSON.parse(window.localStorage.getItem('dashboard_column_setup'))
      if (!isEmpty(storedHeaderColumnGroups)) {
        this.listHeaderColumnGroups = this.cloneArray(this.listHeaderColumnGroupsOrig)
        for (let i = 0; i < this.listHeaderColumnGroups.length; i++) {
          const columnGroup = this.listHeaderColumnGroups[i]
          for (let j = 0; j < columnGroup.columns.length; j++) {
            const element = columnGroup.columns[j]
            if (storedHeaderColumnGroups[i] && storedHeaderColumnGroups[i].columns && storedHeaderColumnGroups[i].columns[j]) {
              element.selected = storedHeaderColumnGroups[i].columns[j].selected
            }
          }
        }
      } else {
        this.listHeaderColumnGroups = this.cloneArray(this.listHeaderColumnGroupsOrig)
        this.save_Column_Setup()
      }
    },
    save_Column_Setup () {
      window.localStorage.setItem('dashboard_column_setup', JSON.stringify(this.listHeaderColumnGroups))
    },
    // Show / hide column
    toggleColumn (isSelected, indexes) {
      this.listHeaderColumnGroups[indexes[0]].columns[indexes[1]].selected = isSelected
      this.save_Column_Setup()
    },
    showColumn (columnName) {
      var selected = false

      // First check if expanded
      if (this.parcelListExpanded) return true

      // Check the setup list
      this.listHeaderColumnGroups.forEach((item, index) => {
        item.columns.forEach((column, index2) => {
          if (column.name === columnName) {
            selected = column.selected
          }
        })
      })

      return selected
    }
  }
}
</script>

<style lang="scss">
  @import '../variables.scss';

  .spl-dashboard-list {
    .padding-wrapper {
      margin-left: -10px;
    }
    .spl-checkbox-group__item__label {
      padding-top: 10px;
    }

    &__controls {
      margin: 0 70px;
      display: flex;
      flex-direction: row;
      &__customer-country {
        flex-grow: 5;
      }
    }

    &__sticky-filters {
      background-color: #fff;
      left: 0;
      position: fixed;
      padding: 0 $mobilePadding;
      top: $headerHeight + 60px;
      z-index: 3000;

      .spl-radiobutton-group-horizontal {
        margin: 7px 0 7px 10px;

        &:first-child,
        &:first-child .spl-radiobutton-group__item:first-child {
          border-left: 0;
          margin-left: 0;
        }
        .spl-radiobutton-group__item {
          margin-left: 12px;
          margin-right: 0;

          &__label {
            font-size: 13px;
            line-height: 15px;

            .spl-radiobutton {
              height: 15px;
              margin-right: 3px;
              width: 15px;

              &:after {
                height: 7px;
                left: 2px;
                top: 2px;
                width: 7px;
              }
            }
          }
        }
      }
    }
    &__selected-date {
      color: $mainInputColor;
      float: right;
      font-size: 16px;
      font-weight: 700;
      line-height: 40px;
      margin-right: 15px;
    }
    &__previous-date,
    &__next-date {
      background-color: $headerNavBorderColor;
      background-image: url(../assets/icon-pagination-prev.svg);
      background-position: 7px center;
      background-repeat: no-repeat;
      border-radius: 50%;
      cursor: pointer;
      float: right;
      margin: 10px 0 0 7px;
      height: 21px;
      width: 21px;

      &-disabled {
        background-color: $paginationButtonColor;
        background-image: url(../assets/icon-pagination-prev-disabled.svg);
      }
    }
    &__next-date {
      background-image: url(../assets/icon-pagination-next.svg);
      background-position: 9px center;

      &-disabled {
        background-image: url(../assets/icon-pagination-next-disabled.svg);
      }
    }
    &__filter,
    &__save-template {
      background-position: 7px center;
      background-repeat: no-repeat;
      border: 2px solid $parcelListControlsBorderColor;
      border-radius: 50%;
      cursor: pointer;
      float: right;
      height: 34px;
      margin: 2px 0 0 10px;
      width: 34px;
    }
    &__filter {
      background-image: url(../assets/icon-filter.svg);
      background-size: 17px 19px;
      position: relative;

      &-active {
        background-color: $middleYellow;
        background-image: url(../assets/icon-filter-active.svg);
      }
    }
    &__save-template {
      background-image: url(../assets/icon-save-template.svg);
      background-size: 20px 18px;

      &-active {
        background-color: $mainTextColor;
        background-image: url(../assets/icon-save-template-active.svg);
      }
    }
    &__filter-overlay {
      background-color: $mainTextColor;
      box-shadow: 0 2px 50px 0 rgba(0, 0, 0, 0.5);
      cursor: auto;
      opacity: 0;
      padding: 40px 30px 30px 30px;
      pointer-events: none;
      position: absolute;
      right: -22px;
      top: 50px;
      transition: opacity $shortTransition ease-in-out;
      width: 380px;
      z-index: 3000;

      &:before {
        border-style: solid;
        border-width: 0 8px 9px 8px;
        border-color: transparent transparent $mainTextColor transparent;
        content: '';
        height: 0;
        position: absolute;
        right: 29px;
        top: -9px;
        width: 0;
      }
      &-open {
        opacity: 1;
        pointer-events: auto;
      }
      &__header {
        &__label {
          color: $headerLatestUpdatesLabelColor;
          font-size: 16px;
          font-weight: 600;
          margin-top: 10px;
        }
        &__buttons {
          padding: 0;

          .spl-button {
            float: right;
            margin: 0 0 0 15px;
          }
        }
      }
      &__options {
        padding-top: 40px;

        .spl-label {
          color: $disabledYellow;
        }
        .spl-checkbox {
          background-color: transparent;
          border: 1px solid $parcelListControlsShowColumnsOptions;

          &:hover {
            border-color: $middleYellow;
          }
          &:checked {
            background-color: $mainInputColor;
            border-color: $middleYellow;

            &:after {
              background-image: url(../assets/icon-checkmark-yellow.svg);
            }
          }
        }
      }
    }
    .spl-h3-yellow {
      height: 40px;
      margin-bottom: 25px;
    }
  }

  @include mobile {
    .spl-dashboard-list {
      & .spl-radiobutton-group-horizontal {
        margin-right: 0px;
        & .spl-radiobutton-group {
          &__item {
            margin-right: 10px;
          }
        }
      }
    }
  }
</style>
